import { useEffect, useState } from 'react';

import { apiRequest } from '@/src/services';
import { getValidSelectValue, getZeroOption, type Option } from '@/src/utils';
import {
    SimpleButton,
    SimpleSingleSelect,
    FilterIcon,
    ICON_COLORS,
    FilterBarContainer,
} from '@/src/ui';
import type { InitialOptions } from '../../users/models/filterDTOs';

interface FilterState {
    districtId: number;
    schoolId: number;
    gradeLevelMasterId: number;
}

interface ClassFiltersProps {
    filterState: FilterState;
    setFilterState: React.Dispatch<React.SetStateAction<FilterState>>;
    onSearch: (districtId: number, schoolId: number, gradeLevelId: number) => void;
}

const ClassFilters: React.FC<ClassFiltersProps> = ({ filterState, setFilterState, onSearch }) => {
    const [districtOptions, setDistrictOptions] = useState<Option[]>([getZeroOption('Loading...')]);
    const [schoolOptions, setSchoolOptions] = useState<Option[]>([getZeroOption('Loading...')]);
    const [gradeOptions, setGradeOptions] = useState<Option[]>([getZeroOption('Loading...')]);
    const [fieldsChanged, setFieldsChanged] = useState(false);

    useEffect(() => {
        async function fetchDistricts() {
            try {
                const res = await apiRequest<InitialOptions>(
                    'GET',
                    'utility/filterData?getDistrict=true'
                );
                const districts = (res.data.districtOptions ? res.data.districtOptions : []).sort(
                    (a: Option, b: Option) => a.name.localeCompare(b.name)
                );
                const options: Option[] = [
                    getZeroOption('All'),
                    ...districts.map((d: Option) => ({ id: d.id, name: d.name })),
                ];
                setDistrictOptions(options);
            } catch {
                setDistrictOptions([getZeroOption('All')]);
            }
        }
        fetchDistricts();
    }, []);

    useEffect(() => {
        async function fetchSchools() {
            try {
                const res = await apiRequest(
                    'GET',
                    `utility/filterData?getSchool=true&getClass=true&districtId=${filterState.districtId}`
                );
                const data = res.data as { schoolOptions?: Option[] };
                const schools = Array.isArray(data.schoolOptions) ? data.schoolOptions : [];
                const options: Option[] = [
                    getZeroOption('All'),
                    ...schools.map((s: Option) => ({ id: s.id, name: s.name })),
                ];
                setSchoolOptions(options);
            } catch {
                setSchoolOptions([getZeroOption('All')]);
            }
        }
        fetchSchools();
    }, [filterState.districtId]);

    useEffect(() => {
        async function fetchGrades() {
            try {
                const res = await apiRequest<InitialOptions>(
                    'GET',
                    'utility/filterData?getUserTypeMaster=true&getGradeLevelMaster=true&getDistrict=true'
                );
                const data = res.data;
                const grades = Array.isArray(data.gradeLevelMasterOptions)
                    ? (data.gradeLevelMasterOptions as Option[]).map((g) => ({
                          id: g.id,
                          name: g.name,
                      }))
                    : [];
                setGradeOptions([getZeroOption('All'), ...grades]);
            } catch {
                setGradeOptions([getZeroOption('All')]);
            }
        }
        fetchGrades();
    }, []);

    return (
        <FilterBarContainer>
            <SimpleSingleSelect
                name="districtId"
                label="District"
                options={districtOptions}
                value={Number(
                    getValidSelectValue(
                        filterState.districtId,
                        districtOptions,
                        districtOptions[0]?.id ?? 0
                    )
                )}
                onChange={(e) => {
                    setFieldsChanged(true);
                    setFilterState((prev) => ({
                        ...prev,
                        districtId: Number(e.target.value),
                        schoolId: 0,
                    }));
                }}
                sx={{ minWidth: 140, maxWidth: 280 }}
            />
            <SimpleSingleSelect
                name="schoolId"
                label="School"
                options={schoolOptions}
                value={Number(
                    getValidSelectValue(
                        filterState.schoolId,
                        schoolOptions,
                        schoolOptions[0]?.id ?? 0
                    )
                )}
                onChange={(e) => {
                    setFieldsChanged(true);
                    setFilterState((prev) => ({ ...prev, schoolId: Number(e.target.value) }));
                }}
                sx={{ minWidth: 140, maxWidth: 280 }}
            />
            <SimpleSingleSelect
                name="gradeLevelId"
                label="Grade"
                options={gradeOptions}
                value={Number(
                    getValidSelectValue(
                        filterState.gradeLevelMasterId,
                        gradeOptions,
                        gradeOptions[0]?.id ?? 0
                    )
                )}
                onChange={(e) => {
                    setFieldsChanged(true);
                    setFilterState((prev) => ({
                        ...prev,
                        gradeLevelMasterId: Number(e.target.value),
                    }));
                }}
                sx={{ minWidth: 140, maxWidth: 280 }}
            />
            <SimpleButton
                onClick={() => {
                    if (typeof onSearch === 'function') {
                        onSearch(
                            filterState.districtId,
                            filterState.schoolId,
                            filterState.gradeLevelMasterId
                        );
                    }
                }}
                color="primary"
                variant={fieldsChanged ? 'outlined' : 'contained'}
            >
                <FilterIcon
                    color={fieldsChanged ? ICON_COLORS.primary : ICON_COLORS.secondary}
                    style={{ width: '16px', height: '16px', marginRight: '8px' }}
                />{' '}
                Filter
            </SimpleButton>
        </FilterBarContainer>
    );
};

export default ClassFilters;
